import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ExternalLink, RotateCcw, ArrowUpCircle, Eye } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useMe,
  useBrief,
  useRequestPreview,
  usePromote,
  useRollback,
  useBuildStatus,
} from '@/lib/brief';

export const Route = createFileRoute('/_app/')({
  staticData: { crumb: 'Pulpit' },
  component: DashboardPage,
});

function DashboardPage() {
  const { session } = Route.useRouteContext();
  if (session.role === 'operator') return null;

  const me = useMe();
  const brief = useBrief();
  const buildStatus = useBuildStatus();
  const requestPreview = useRequestPreview();
  const promote = usePromote();
  const rollback = useRollback();

  const [feedback, setFeedback] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const tenant = me.data?.tenant;
  const onPreview = async () => {
    setFeedback(null);
    try {
      const r = await requestPreview.mutateAsync();
      setFeedback({
        kind: 'ok',
        text: `Zlecono budowę podglądu. Po wygenerowaniu dostępny pod ${(r as { preview_url?: string }).preview_url ?? ''}.`,
      });
    } catch {
      setFeedback({ kind: 'err', text: 'Nie udało się zlecić podglądu.' });
    }
  };
  const onPromote = async () => {
    setFeedback(null);
    try {
      await promote.mutateAsync();
      setFeedback({ kind: 'ok', text: 'Opublikowano nową wersję strony.' });
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    }
  };
  const onRollback = async () => {
    setFeedback(null);
    try {
      await rollback.mutateAsync();
      setFeedback({ kind: 'ok', text: 'Cofnięto do poprzedniej wersji.' });
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    }
  };

  // Checklist completeness — drives "next steps" prompts.
  const checklist = React.useMemo(() => {
    const b = brief.data ?? {};
    return [
      {
        id: 'basics',
        label: 'Uzupełnij dane podstawowe (NIP, adres, godziny)',
        done: !!(b?.legal?.nip && b?.address?.city && (b?.hours?.length ?? 0) > 0),
        to: '/brief',
      },
      {
        id: 'team',
        label: 'Dodaj zespół',
        done: (b?.team?.length ?? 0) > 0,
        to: '/brief/team',
      },
      {
        id: 'services',
        label: 'Dodaj usługi',
        done: (b?.inline_services?.length ?? 0) > 0,
        to: '/brief/services',
      },
      {
        id: 'faq',
        label: 'Wpisz 3 najczęstsze pytania',
        done: (b?.faq?.length ?? 0) >= 3,
        to: '/brief/faq',
      },
    ];
  }, [brief.data]);

  const previewVersion = tenant?.preview_version ?? 0;
  const liveVersion = tenant?.live_version ?? null;
  const previewHost = tenant?.preview_token
    ? `${tenant.preview_token}.pozyskajpacjenta.pl`
    : null;
  const liveHost = tenant?.custom_host ?? `${tenant?.slug}.pozyskajpacjenta.pl`;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">Pulpit</h1>
        <p className="mt-1 text-sm text-text-muted">
          Stan strony, najbliższe zadania i skróty do publikacji.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Status strony"
          value={
            liveVersion
              ? 'Live'
              : previewVersion > 0
              ? 'Preview'
              : 'Provisioning'
          }
          hint={
            liveVersion
              ? `wersja ${liveVersion}`
              : previewVersion > 0
              ? 'Podgląd gotowy do publikacji'
              : 'Wygeneruj pierwszy podgląd'
          }
        />
        <KpiCard
          title="Plan"
          value={(tenant?.tier ?? 'preview').toUpperCase()}
          hint="Zarządzaj subskrypcją"
        />
        <KpiCard
          title="Nowe leady (7 dni)"
          value="—"
          hint="Formularz po pierwszej publikacji"
        />
        <KpiCard
          title="Wizyty (7 dni)"
          value="—"
          hint="Włącz kalendarz w M3"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Publikacja</CardTitle>
          <CardDescription>
            Po edycji treści zleć budowę podglądu, sprawdź wynik i opublikuj.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-md bg-surface p-4 ring-1 ring-black/5">
              <p className="text-xs uppercase tracking-wide text-text-subtle">Podgląd</p>
              <p className="mt-1 font-display text-base font-semibold text-text">
                {previewVersion > 0 ? `wersja ${previewVersion}` : 'brak'}
              </p>
              {previewHost ? (
                <a
                  href={`https://${previewHost}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-sm text-accent hover:underline"
                >
                  {previewHost}
                  <ExternalLink className="size-3.5" aria-hidden="true" />
                </a>
              ) : null}
            </div>
            <div className="rounded-md bg-surface p-4 ring-1 ring-black/5">
              <p className="text-xs uppercase tracking-wide text-text-subtle">Live</p>
              <p className="mt-1 font-display text-base font-semibold text-text">
                {liveVersion ? `wersja ${liveVersion}` : 'jeszcze nie opublikowano'}
              </p>
              {liveVersion ? (
                <a
                  href={`https://${liveHost}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-sm text-accent hover:underline"
                >
                  {liveHost}
                  <ExternalLink className="size-3.5" aria-hidden="true" />
                </a>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={onPreview} disabled={requestPreview.isPending}>
              <Eye className="size-4" aria-hidden="true" />
              {requestPreview.isPending ? 'Zlecanie…' : 'Zleć budowę podglądu'}
            </Button>
            <Button
              variant="secondary"
              onClick={onPromote}
              disabled={previewVersion <= 0 || promote.isPending || previewVersion === liveVersion}
            >
              <ArrowUpCircle className="size-4" aria-hidden="true" />
              Opublikuj podgląd jako live
            </Button>
            <Button
              variant="ghost"
              onClick={onRollback}
              disabled={!liveVersion || liveVersion <= 1 || rollback.isPending}
            >
              <RotateCcw className="size-4" aria-hidden="true" />
              Cofnij do poprzedniej
            </Button>
            <Link
              to={'/publish' as never}
              className="ml-auto text-sm text-accent hover:underline"
            >
              Pełna historia wersji →
            </Link>
          </div>
          {feedback ? (
            <p
              className={
                feedback.kind === 'ok'
                  ? 'rounded-md bg-success/10 p-3 text-sm text-success ring-1 ring-success/20'
                  : 'rounded-md bg-error/10 p-3 text-sm text-error ring-1 ring-error/20'
              }
            >
              {feedback.text}
            </p>
          ) : null}

          {buildStatus.data?.latest ? (
            <div className="rounded-md bg-surface p-4 ring-1 ring-black/5">
              <p className="text-xs uppercase tracking-wide text-text-subtle">
                Ostatnia budowa
              </p>
              <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-display text-base font-semibold text-text">
                  wersja {buildStatus.data.latest.version}
                </p>
                <BuildStatusBadge status={buildStatus.data.latest.status} />
              </div>
              {buildStatus.data.latest.status === 'failed' && buildStatus.data.latest.error ? (
                <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-bg p-2 text-xs text-error ring-1 ring-error/20">
                  {buildStatus.data.latest.error}
                </pre>
              ) : null}
              {buildStatus.data.latest.status === 'ready' &&
              buildStatus.data.latest.page_count ? (
                <p className="mt-1 text-xs text-text-muted">
                  Zbudowano {buildStatus.data.latest.page_count} stron.{' '}
                  {/* llm_model intentionally not rendered — internal know-how */}
                </p>
              ) : null}
              {buildStatus.data.latest.status === 'requested' ||
              buildStatus.data.latest.status === 'building' ? (
                <p className="mt-2 text-xs text-text-muted">
                  Budowa w toku — odświeżamy automatycznie co 10 sekund.
                </p>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Następne kroki</CardTitle>
          <CardDescription>
            Te elementy są wymagane do pełnej, gotowej-do-publikacji strony.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul role="list" className="space-y-2 text-sm">
            {checklist.map((step) => (
              <li key={step.id}>
                <Link
                  to={step.to as never}
                  className="flex items-center justify-between rounded-md bg-surface p-3 ring-1 ring-black/5 hover:ring-black/10"
                >
                  <span className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className={
                        step.done
                          ? 'inline-grid size-5 place-items-center rounded-full bg-success/20 text-success'
                          : 'inline-grid size-5 place-items-center rounded-full bg-bg ring-1 ring-border'
                      }
                    >
                      {step.done ? '✓' : ''}
                    </span>
                    <span className={step.done ? 'text-text-muted line-through' : 'text-text'}>
                      {step.label}
                    </span>
                  </span>
                  <span className="text-text-subtle text-xs">otwórz →</span>
                </Link>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function BuildStatusBadge({ status }: { status: 'requested' | 'building' | 'ready' | 'failed' }) {
  const styles: Record<typeof status, { bg: string; label: string }> = {
    requested: { bg: 'bg-warning/10 text-warning ring-warning/20', label: 'w kolejce' },
    building: { bg: 'bg-accent/10 text-accent ring-accent/20', label: 'budowanie…' },
    ready: { bg: 'bg-success/10 text-success ring-success/20', label: 'gotowe' },
    failed: { bg: 'bg-error/10 text-error ring-error/20', label: 'błąd' },
  };
  const s = styles[status];
  return <span className={`rounded-full px-2 py-0.5 text-xs ring-1 ${s.bg}`}>{s.label}</span>;
}

function KpiCard({ title, value, hint }: { title: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="px-5 pt-5 pb-5">
        <p className="text-xs font-medium uppercase tracking-wide text-text-subtle">{title}</p>
        <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-text tabular-nums">
          {value}
        </p>
        {hint ? <p className="mt-1 text-xs text-text-subtle">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}
